/**
 * INVENTARIOS FÍSICOS (FASE 9.3)
 *
 *   /inventario/inventarios        listado + «Nuevo inventario»
 *   /inventario/inventarios/:id    ficha: Información · Conteo · Diferencias · Historial
 *
 * El inventario NO modifica el saldo: cuenta y, al cerrar, la base genera
 * una operación AJUSTE (visible en Movimientos). La pantalla de conteo es
 * mobile-first: tarjetas, cantidad + unidad, «Guardar y siguiente». Con
 * conteo ciego la API no envía teórico ni diferencia: aquí no se esconde
 * nada que haya llegado, simplemente no llega.
 *
 * FASE 9.4: los artículos con lotes se cuentan POR LOTE (una tarjeta por
 * lote, con su caducidad; «Añadir lote encontrado» para el que no se
 * esperaba). Los serializados se cuentan POR IDENTIDAD: se escanean o pegan
 * las series encontradas; en validación se ven faltantes e inesperadas, y el
 * cierre las regulariza una a una.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  api,
  FalloApi,
  type AlcanceInventario,
  type ConteoInventarioFisico,
  type EstadoInventarioFisico,
  type EventoInventarioFisico,
  type InventarioFisicoDetalle,
  type InventarioFisicoResumen,
  type LineaInventarioFisico,
  type OpcionAlmacenStock,
  type OpcionArticuloStock,
  type OpcionLote,
} from '../api.ts';
import { BarraAcciones, Cifra, EmptyState, Error as ErrorCaja, Estado as EstadoBadge, fechaHora, FiltrosResponsive, Modal, Panel, SinResultados, useDatos, Vacio } from '../componentes/base.tsx';
import { CambiosEvento } from '../componentes/cambios-historial.tsx';
import { ESTADOS_INVENTARIO as ESTADOS_INVENTARIO_CENTRAL, estadoInventario } from '../estados.ts';
import { Buscador } from '../componentes/buscador.tsx';
import { Escaner } from '../componentes/escaner.tsx';
import { anadirSerie, sumar } from '../escaner/opciones.ts';
import type { Lectura } from '../escaner/tipos.ts';
import { PieHistorial, useHistorial } from '../componentes/historial.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useToast } from '../componentes/toast.tsx';
import { useSesion } from '../contexto.tsx';
import { buscarArticulos, erroresDe, ErrorStock, nombreAlmacen, nombreArticulo, Paginacion, Pestanas } from './stock.tsx';
import { EditorSeries, EtiquetaCaducidad, caducidadDe, SelectorLote, seriesUnicas } from './trazabilidad.tsx';

//  UX-1C: [etiqueta, tono] del mapa central (`estados.ts`).
export const ESTADOS_INVENTARIO = Object.fromEntries(
  Object.entries(ESTADOS_INVENTARIO_CENTRAL).map(([k, x]) => [k, [x.label, x.tone]]),
) as Readonly<Record<EstadoInventarioFisico, [string, string]>>;
const ALCANCE: Readonly<Record<AlcanceInventario, string>> = { TOTAL: 'Total', PARCIAL: 'Parcial' };
export const TEXTO_INICIAR = 'Al iniciar, el alcance del inventario quedará fijado. El almacén podrá seguir operando durante el conteo.';
export const TEXTO_CIERRE = 'El inventario no modifica el saldo directamente. La regularización quedará registrada en el histórico de movimientos.';

const Estado = ({ e }: { e: EstadoInventarioFisico }): ReactNode => <EstadoBadge d={estadoInventario(e)} />;
const pct = (a: number, b: number): number => (b === 0 ? 0 : Math.round((a * 100) / b));
const POR_PAGINA = 50;
/** UX-1C — feedback ligero de cada transición (los errores siguen en la ficha). */
const EXITO_INVENTARIO: Readonly<Record<string, string>> = {
  iniciar: 'Inventario iniciado.', 'enviar-validacion': 'Inventario enviado a validación.', 'volver-a-conteo': 'Inventario devuelto a conteo.',
  cerrar: 'Inventario cerrado y regularizado.', anular: 'Inventario anulado.',
};
const POR_PAGINA_CONTEO = 25;

function Progreso({ contadas, total }: { contadas: number; total: number }): ReactNode {
  return (
    <div className="progreso-inventario">
      <span>{contadas} / {total} artículos contados · {pct(contadas, total)} %</span>
      <div className="progreso-barra" role="progressbar" aria-label="Progreso del conteo" aria-valuemin={0} aria-valuemax={total} aria-valuenow={contadas}>
        <div style={{ width: `${String(pct(contadas, total))}%` }} />
      </div>
    </div>
  );
}

// ================================================================== listado

/** Almacenes en los que se puede ABRIR un inventario: ámbito de stock y sin otro inventario abierto (lo filtra la API). */
const buscarAlmacenesParaInventario = async (texto: string): Promise<OpcionAlmacenStock[]> =>
  (await api.get<{ filas: OpcionAlmacenStock[] }>(`/api/inventario/opciones/almacenes?${new URLSearchParams({ texto, limite: '20', paraInventario: 'true' }).toString()}`)).filas;

function NuevoInventario({ cerrar }: { cerrar: () => void }): ReactNode {
  const navegar = useNavigate();
  const [almacen, setAlmacen] = useState<OpcionAlmacenStock | null>(null);
  const [alcance, setAlcance] = useState<AlcanceInventario>('TOTAL');
  const [ciego, setCiego] = useState(true);
  const [observaciones, setObservaciones] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const campos = erroresDe(error);
  async function crear(): Promise<void> {
    setError(null);
    setEnviando(true);
    try {
      const r = await api.post<InventarioFisicoDetalle>('/api/inventario/inventarios', { idAlmacen: almacen?.id ?? null, alcance, conteoCiego: ciego, observaciones });
      navegar(`/inventario/inventarios/${r.id}`);
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }
  return (
    <Modal titulo="Nuevo inventario" cerrar={cerrar}
      pie={<><button onClick={cerrar}>Cancelar</button><button className="principal" disabled={almacen === null || enviando} onClick={() => void crear()}>Crear borrador</button></>}>
      {error !== null && Object.keys(campos).length === 0 && <ErrorCaja error={error} />}
      <Buscador<OpcionAlmacenStock> etiqueta="Almacén" valor={almacen} alElegir={setAlmacen} buscar={buscarAlmacenesParaInventario}
        clave={(a) => a.id} texto={nombreAlmacen} error={campos['idAlmacen']}
        sinResultados="No hay almacenes disponibles. Sólo aparecen almacenes activos e inventariables de tus centros que no tengan ya un inventario abierto." />
      <fieldset className="campo">
        <legend>Alcance</legend>
        <label className="casilla"><input type="radio" name="alcance" checked={alcance === 'TOTAL'} onChange={() => setAlcance('TOTAL')} /> Total — todos los artículos configurados en el almacén al iniciar</label>
        <label className="casilla"><input type="radio" name="alcance" checked={alcance === 'PARCIAL'} onChange={() => setAlcance('PARCIAL')} /> Parcial — eliges los artículos en el borrador</label>
      </fieldset>
      <label className="casilla"><input type="checkbox" checked={ciego} onChange={(ev) => setCiego(ev.target.checked)} /> Conteo ciego (quien cuenta no ve el stock teórico)</label>
      <div className="campo"><label htmlFor="ni-obs">Observaciones</label>
        <textarea id="ni-obs" rows={2} maxLength={2000} value={observaciones} onChange={(ev) => setObservaciones(ev.target.value)} /></div>
      <p className="suave pequena">Se crea como borrador: no afecta al stock.</p>
    </Modal>
  );
}

export function PaginaInventariosFisicos(): ReactNode {
  const { puede } = useSesion();
  const [q, setQ] = useState({ texto: '', estado: '', alcance: '' });
  const [pagina, setPagina] = useState(1);
  const [nuevo, setNuevo] = useState(false);
  const datos = useDatos(() => {
    const p = new URLSearchParams({ pagina: String(pagina), porPagina: String(POR_PAGINA) });
    for (const [k, v] of Object.entries(q)) if (v !== '') p.set(k, v);
    return api.get<{ filas: InventarioFisicoResumen[]; total: number }>(`/api/inventario/inventarios?${p.toString()}`);
  }, [q.texto, q.estado, q.alcance, pagina]);
  const fijar = (k: keyof typeof q, v: string): void => { setQ((a) => ({ ...a, [k]: v })); setPagina(1); };
  return (
    <Shell migas={[{ texto: 'Inventario' }, { texto: 'Inventarios' }]} titulo="Inventarios"
      subtitulo="Conteos físicos. Las diferencias se regularizan con una operación de ajuste al cerrar."
      acciones={puede('inventarios:crear') ? <button className="principal" onClick={() => setNuevo(true)}>Nuevo inventario</button> : undefined}>
      <section className="panel" aria-label="Inventarios">
        <div className="panel-cuerpo">
          <FiltrosResponsive contador={[q.texto, q.estado, q.alcance].filter((x) => x !== '').length}>
            <div className="filtros-rejilla">
              <div className="campo"><label htmlFor="inv-texto">Buscar</label>
                <input id="inv-texto" type="search" placeholder="Código o almacén" value={q.texto} onChange={(ev) => fijar('texto', ev.target.value)} /></div>
              <label className="campo"><span>Estado</span>
                <select value={q.estado} onChange={(ev) => fijar('estado', ev.target.value)}>
                  <option value="">Todos</option>
                  {(Object.keys(ESTADOS_INVENTARIO) as EstadoInventarioFisico[]).map((e) => <option key={e} value={e}>{ESTADOS_INVENTARIO[e][0]}</option>)}
                </select>
              </label>
              <label className="campo"><span>Alcance</span>
                <select value={q.alcance} onChange={(ev) => fijar('alcance', ev.target.value)}>
                  <option value="">Todos</option><option value="TOTAL">Total</option><option value="PARCIAL">Parcial</option>
                </select>
              </label>
            </div>
          </FiltrosResponsive>
          <Panel estado={datos}>
            {(d) => (d.filas.length === 0 ? (Object.values(q).some((x) => x !== '')
            ? <SinResultados limpiar={() => { setQ({ texto: '', estado: '', alcance: '' }); setPagina(1); }} />
            : <EmptyState titulo="Sin inventarios" descripcion={puede('inventarios:crear') ? 'Crea uno con «Nuevo inventario».' : 'Todavía no se ha hecho ningún inventario físico.'} />) : (
              <>
                <div className="tabla-desliza">
                  <table aria-label="Inventarios físicos" className="tabla-tarjetas">
                    <thead><tr><th>Código</th><th>Almacén</th><th>Alcance</th><th>Estado</th><th>Progreso</th><th>Creado</th><th>Iniciado</th><th>Cerrado</th></tr></thead>
                    <tbody>
                      {d.filas.map((i) => (
                        <tr key={i.id}>
                          <td className="mono celda-titulo"><Link to={`/inventario/inventarios/${i.id}`}>{i.codigo}</Link></td>
                          <td data-label="Almacén">{nombreAlmacen(i.almacen)}</td>
                          <td data-label="Alcance">{ALCANCE[i.alcance]}{i.conteoCiego && <span className="suave pequena"> · ciego</span>}</td>
                          <td data-label="Estado"><Estado e={i.estado} /></td>
                          <td className="pequena" data-label="Progreso">{i.estado === 'BORRADOR' ? '—' : `${String(i.progreso.contadas)} / ${String(i.progreso.total)} (${String(pct(i.progreso.contadas, i.progreso.total))} %)`}</td>
                          <td className="pequena" data-label="Creado">{fechaHora(i.creadoEn)}</td>
                          <td className="pequena" data-label="Iniciado">{fechaHora(i.iniciadoEn)}</td>
                          <td className="pequena" data-label="Cerrado">{fechaHora(i.cerradoEn)}{i.operacionAjuste !== null && <> · <Link to={`/inventario/operaciones/${i.operacionAjuste.id}`}>{i.operacionAjuste.codigo}</Link></>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Paginacion pagina={pagina} hayMas={pagina * POR_PAGINA < d.total} fijar={setPagina} total={d.total} porPagina={POR_PAGINA} />
              </>
            ))}
          </Panel>
        </div>
      </section>
      {nuevo && <NuevoInventario cerrar={() => setNuevo(false)} />}
    </Shell>
  );
}

// ======================================================== ficha: información

function Dato({ e, v }: { e: string; v: ReactNode }): ReactNode {
  return <div className="dato"><div className="etiqueta-dato">{e}</div><div className="valor-dato">{v === null || v === '' ? '—' : v}</div></div>;
}

function EditarBorrador({ inv, alCambiar }: { inv: InventarioFisicoDetalle; alCambiar: () => void }): ReactNode {
  const [v, setV] = useState({ alcance: inv.alcance, conteoCiego: inv.conteoCiego, observaciones: inv.observaciones ?? '' });
  const [error, setError] = useState<unknown>(null);
  const cambiado = v.alcance !== inv.alcance || v.conteoCiego !== inv.conteoCiego || v.observaciones.trim() !== (inv.observaciones ?? '');
  async function guardar(): Promise<void> {
    setError(null);
    try {
      await api.patch(`/api/inventario/inventarios/${inv.id}`, { ...v, versionEsperada: inv.actualizadoEn });
      alCambiar();
    } catch (e) {
      setError(e);
    }
  }
  return (
    <form aria-label="Editar borrador del inventario" className="cabecera-borrador" onSubmit={(ev) => { ev.preventDefault(); void guardar(); }}>
      {error !== null && <ErrorCaja error={error} />}
      <fieldset className="campo">
        <legend>Alcance</legend>
        <label className="casilla"><input type="radio" name="ed-alcance" checked={v.alcance === 'TOTAL'} onChange={() => setV({ ...v, alcance: 'TOTAL' })} /> Total</label>
        <label className="casilla"><input type="radio" name="ed-alcance" checked={v.alcance === 'PARCIAL'} onChange={() => setV({ ...v, alcance: 'PARCIAL' })} /> Parcial</label>
        {inv.alcance === 'PARCIAL' && v.alcance === 'TOTAL' && inv.resumen.total > 0 && <p className="suave pequena">Al pasar a Total se descartan los artículos elegidos.</p>}
      </fieldset>
      <label className="casilla"><input type="checkbox" checked={v.conteoCiego} onChange={(ev) => setV({ ...v, conteoCiego: ev.target.checked })} /> Conteo ciego</label>
      <div className="campo"><label htmlFor="ed-obs">Observaciones</label>
        <textarea id="ed-obs" rows={2} maxLength={2000} value={v.observaciones} onChange={(ev) => setV({ ...v, observaciones: ev.target.value })} /></div>
      <div className="fila"><button type="submit" className="principal" disabled={!cambiado}>Guardar cambios</button></div>
    </form>
  );
}

function ArticulosParciales({ inv, edita, alCambiar }: { inv: InventarioFisicoDetalle; edita: boolean; alCambiar: () => void }): ReactNode {
  const [error, setError] = useState<unknown>(null);
  const [art, setArt] = useState<OpcionArticuloStock | null>(null);
  const lineas = useDatos(() => api.get<{ filas: LineaInventarioFisico[]; total: number }>(`/api/inventario/inventarios/${inv.id}/lineas?porPagina=200`), [inv.id, inv.actualizadoEn, inv.resumen.total]);
  async function accion(f: () => Promise<unknown>): Promise<void> {
    setError(null);
    try {
      await f();
      setArt(null);
      alCambiar();
    } catch (e) {
      setError(e);
    }
  }
  return (
    <section className="panel" aria-label="Artículos del inventario">
      <div className="panel-cuerpo">
        <h2 className="titulo-seccion">Artículos a contar ({inv.resumen.total})</h2>
        {error !== null && <ErrorCaja error={error} />}
        {edita && (
          <div className="form-rejilla">
            <Buscador<OpcionArticuloStock> etiqueta="Añadir artículo" valor={art} alElegir={(a) => { setArt(a); if (a !== null) void accion(() => api.post(`/api/inventario/inventarios/${inv.id}/articulos`, { idArticulo: a.id })); }}
              buscar={buscarArticulos(inv.almacen.id)} clave={(a) => a.id} texto={nombreArticulo} contexto={inv.almacen.id}
              sinResultados="Ningún artículo activo configurado en este almacén." placeholder="Código o descripción" />
          </div>
        )}
        <Panel estado={lineas}>
          {(d) => (d.filas.length === 0 ? <p className="suave">Todavía no hay artículos elegidos.</p> : (
            <ul className="lista-simple">
              {d.filas.map((l) => (
                <li key={l.id} className="fila" style={{ justifyContent: 'space-between' }}>
                  <span>{nombreArticulo(l.articulo)}</span>
                  {edita && <button className="pequeno" onClick={() => void accion(() => api.del(`/api/inventario/inventarios/${inv.id}/articulos/${l.articulo.id}`))}>Quitar</button>}
                </li>
              ))}
            </ul>
          ))}
        </Panel>
      </div>
    </section>
  );
}

// ============================================================ ficha: conteo

function HistorialConteos({ inv, linea }: { inv: InventarioFisicoDetalle; linea: LineaInventarioFisico }): ReactNode {
  const d = useDatos(() => api.get<{ filas: ConteoInventarioFisico[] }>(`/api/inventario/inventarios/${inv.id}/lineas/${linea.id}/conteos`), [inv.id, linea.id, linea.nConteos]);
  return (
    <Panel estado={d}>
      {(x) => (
        <ol className="lista-conteos" aria-label={`Conteos de ${linea.articulo.codigo}`}>
          {x.filas.map((c) => (
            <li key={c.id}>
              <strong>Conteo #{c.numero}: {c.cantidad} {linea.serializado === true ? 'serie(s)' : c.unidad}</strong>{linea.serializado !== true && c.unidad !== linea.unidadStock.codigo && ` (= ${c.cantidadStock} ${linea.unidadStock.codigo})`}
              {c.teorico !== null && <span className="suave"> · teórico {c.teorico} · diferencia {c.diferencia}</span>}
              {(c.seriesDiscrepantes ?? 0) > 0 && <span className="suave"> · {c.seriesDiscrepantes} serie(s) no cuadran</span>}
              <div className="suave pequena">{c.contadoPor.nombre} · {fechaHora(c.contadoEn)}{c.observaciones !== null && ` · ${c.observaciones}`}</div>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}

/** F13.3: lectura del escáner dirigida a una línea (cantidad que se suma o serie que se añade). */
interface LecturaConteo { idLinea: string; cantidad: number | null; serie: string | null; k: number }

function TarjetaConteo({ inv, linea, cuenta, alGuardar, refInput, lectura }: {
  inv: InventarioFisicoDetalle; linea: LineaInventarioFisico; cuenta: boolean;
  alGuardar: (l: LineaInventarioFisico, siguiente: boolean) => void; refInput: (el: HTMLInputElement | null) => void;
  lectura?: LecturaConteo | null;
}): ReactNode {
  const [cantidad, setCantidad] = useState('');
  const [idUnidad, setIdUnidad] = useState(linea.unidadStock.id);
  const local = useRef<HTMLInputElement | null>(null);
  //  F13.3: cada lectura suma su cantidad (en unidad de stock) y deja el foco en la cantidad para corregirla.
  useEffect(() => {
    if (lectura == null || lectura.idLinea !== linea.id || linea.serializado === true) return;
    setIdUnidad(linea.unidadStock.id);
    setCantidad((c) => sumar(c, lectura.cantidad ?? 1));
    local.current?.scrollIntoView?.({ block: 'center' });
  }, [lectura?.k]);
  const [observaciones, setObservaciones] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [verConteos, setVerConteos] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const campos = erroresDe(error);
  const v = linea.vigente;
  async function guardar(siguiente: boolean): Promise<void> {
    setError(null);
    setGuardando(true);
    try {
      const r = await api.post<LineaInventarioFisico>(`/api/inventario/inventarios/${inv.id}/lineas/${linea.id}/conteos`, { cantidad, idUnidad, observaciones });
      setCantidad('');
      setObservaciones('');
      alGuardar(r, siguiente);
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }
  const id = `c-${linea.id}`;
  return (
    <article className={`tarjeta-conteo estado-${linea.estadoConteo.toLowerCase()}`} aria-label={`${linea.articulo.codigo} · ${linea.articulo.descripcion}`}>
      <header className="fila" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div><span className="mono">{linea.articulo.codigo}</span> <strong>{linea.articulo.descripcion}</strong>{!linea.articulo.activo && <span className="etiqueta oscura">Inactivo</span>}
          {linea.lote != null && (
            <div className="pequena">Lote <span className="mono">{linea.lote.codigo}</span>{' '}
              <EtiquetaCaducidad fechaCaducidad={linea.lote.fechaCaducidad} caducidad={caducidadDe(linea.lote.fechaCaducidad, linea.lote.caducado)} /></div>
          )}</div>
        <span className={`etiqueta ${linea.estadoConteo === 'PENDIENTE' ? 'neutra' : linea.estadoConteo === 'RECONTADA' ? 'aviso' : 'exito-suave'}`}>
          {linea.estadoConteo === 'PENDIENTE' ? 'Pendiente' : linea.estadoConteo === 'RECONTADA' ? 'Recontada' : 'Contada'}
        </span>
      </header>
      {linea.serializado === true
        ? <ConteoSeries inv={inv} linea={linea} cuenta={cuenta} alGuardar={alGuardar} lectura={lectura?.idLinea === linea.id ? lectura : null} />
        : <>
      <p className="suave pequena">Unidad de stock: {linea.unidadStock.codigo}
        {linea.saldoActual !== null && !inv.veDiferencias && <> · Stock teórico (referencia): <strong>{linea.saldoActual} {linea.unidadStock.codigo}</strong></>}</p>
      {v !== null && (
        <p className="pequena">Último conteo: <strong>{v.cantidad} {v.unidad}</strong>{v.unidad !== linea.unidadStock.codigo && ` (= ${v.cantidadStock} ${linea.unidadStock.codigo})`}
          <span className="suave"> · {v.contadoPor.nombre} · {fechaHora(v.contadoEn)}</span>
          {linea.nConteos > 1 && <span className="suave"> · {linea.nConteos} conteos</span>}</p>
      )}
      {cuenta && (
        <form className="form-conteo" aria-label={`Contar ${linea.articulo.codigo}`} onSubmit={(ev) => { ev.preventDefault(); void guardar(false); }}>
          {error !== null && Object.keys(campos).length === 0 && <ErrorCaja error={error} />}
          <div className="form-rejilla">
            <div className="campo">
              <label htmlFor={`${id}-cant`}>{v === null ? 'Cantidad contada' : 'Nueva cantidad (reconteo)'}</label>
              <input id={`${id}-cant`} ref={(el) => { local.current = el; refInput(el); }} inputMode="decimal" autoComplete="off" value={cantidad} onChange={(ev) => setCantidad(ev.target.value)} />
              {campos['cantidad'] !== undefined && <div className="error-campo">{campos['cantidad']}</div>}
            </div>
            <div className="campo">
              <label htmlFor={`${id}-ud`}>Unidad de conteo</label>
              <select id={`${id}-ud`} value={idUnidad} onChange={(ev) => setIdUnidad(ev.target.value)}>
                {linea.unidades.map((u) => <option key={u.id} value={u.id}>{u.codigo}{u.factor === '1' ? '' : ` (= ${u.factor} ${linea.unidadStock.codigo})`}</option>)}
              </select>
            </div>
            <div className="campo">
              <label htmlFor={`${id}-obs`}>Observaciones</label>
              <input id={`${id}-obs`} maxLength={1000} value={observaciones} onChange={(ev) => setObservaciones(ev.target.value)} />
            </div>
          </div>
          <div className="fila acciones-conteo">
            <button type="submit" disabled={cantidad.trim() === '' || guardando}>{v === null ? 'Registrar conteo' : 'Recontar'}</button>
            <button type="button" className="principal" disabled={cantidad.trim() === '' || guardando} onClick={() => void guardar(true)}>Guardar y siguiente</button>
          </div>
        </form>
      )}
      </>}
      {linea.nConteos > 0 && (
        <>
          <button type="button" className="enlace pequena" aria-expanded={verConteos} onClick={() => setVerConteos(!verConteos)}>{verConteos ? 'Ocultar conteos' : 'Ver conteos'}</button>
          {verConteos && <HistorialConteos inv={inv} linea={linea} />}
        </>
      )}
    </article>
  );
}

/** Lista corta de números de serie (con «y N más» si son muchas). */
function ListaNumeros({ etiqueta, numeros, clase = '' }: { etiqueta: string; numeros: string[]; clase?: string }): ReactNode {
  if (numeros.length === 0) return null;
  return (
    <div className={`pequena ${clase}`}><strong>{etiqueta} ({numeros.length}):</strong> <span className="mono">{numeros.slice(0, 50).join(', ')}{numeros.length > 50 ? ` y ${String(numeros.length - 50)} más` : ''}</span></div>
  );
}

/**
 * FASE 9.4 — conteo por IDENTIDAD de un artículo serializado: se registran las
 * series encontradas (escáner o pegado; una por línea). La API calcula
 * esperadas / faltantes / inesperadas en la frontera del conteo; en conteo
 * ciego sólo se ve lo contado.
 */
function ConteoSeries({ inv, linea, cuenta, alGuardar, lectura }: {
  inv: InventarioFisicoDetalle; linea: LineaInventarioFisico; cuenta: boolean; alGuardar: (l: LineaInventarioFisico, siguiente: boolean) => void;
  lectura?: LecturaConteo | null;
}): ReactNode {
  const [texto, setTexto] = useState('');
  useEffect(() => {
    if (lectura?.serie == null) return;
    setTexto((t) => anadirSerie(t, lectura.serie!) ?? t);
  }, [lectura?.k]);
  const [observaciones, setObservaciones] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [guardando, setGuardando] = useState(false);
  const campos = erroresDe(error);
  const v = linea.vigente;
  const sr = linea.series ?? null;
  async function guardar(): Promise<void> {
    setError(null);
    setGuardando(true);
    try {
      const r = await api.post<LineaInventarioFisico>(`/api/inventario/inventarios/${inv.id}/lineas/${linea.id}/conteos-series`, { series: seriesUnicas(texto), observaciones });
      setTexto('');
      setObservaciones('');
      alGuardar(r, false);
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }
  const id = `cs-${linea.id}`;
  return (
    <>
      <p className="suave pequena">Se cuenta por número de serie (1 serie = 1 {linea.unidadStock.codigo}).</p>
      {linea.seriesEnStock != null && (
        <div className="nota pequena">
          <ListaNumeros etiqueta="En stock según el sistema (referencia)" numeros={linea.seriesEnStock} />
          {cuenta && linea.seriesEnStock.length > 0 && texto.trim() === '' && (
            <button type="button" className="pequeno" onClick={() => setTexto(linea.seriesEnStock!.join('\n'))}>Partir de esta lista</button>
          )}
        </div>
      )}
      {v !== null && sr !== null && (
        <div className="pequena">
          <p>Último conteo: <strong>{sr.encontradas.length} serie(s) encontradas</strong><span className="suave"> · {v.contadoPor.nombre} · {fechaHora(v.contadoEn)}</span>
            {sr.esperadas !== null && <span className="suave"> · esperadas {sr.esperadas}</span>}</p>
          {sr.faltantes !== null && <ListaNumeros etiqueta="Faltantes" numeros={sr.faltantes} clase="saldo-negativo" />}
          {sr.inesperadas !== null && <ListaNumeros etiqueta="Inesperadas" numeros={sr.inesperadas} clase="aviso-texto" />}
          {sr.faltantes === null && <ListaNumeros etiqueta="Encontradas" numeros={sr.encontradas} />}
        </div>
      )}
      {cuenta && (
        <form className="form-conteo" aria-label={`Contar series de ${linea.articulo.codigo}`} onSubmit={(ev) => { ev.preventDefault(); void guardar(); }}>
          {error !== null && Object.keys(campos).length === 0 && <ErrorCaja error={error} />}
          <EditorSeries id={`${id}-series`} etiqueta={v === null ? 'Series encontradas' : 'Series encontradas (reconteo: la lista completa)'} valor={texto} cambiar={setTexto} error={campos['series']} />
          <div className="campo">
            <label htmlFor={`${id}-obs`}>Observaciones</label>
            <input id={`${id}-obs`} maxLength={1000} value={observaciones} onChange={(ev) => setObservaciones(ev.target.value)} />
          </div>
          <p className="suave pequena">Si no hay ninguna unidad, registra el conteo con la lista vacía.</p>
          <div className="fila acciones-conteo">
            <button type="submit" className="principal" disabled={guardando}>{v === null ? 'Registrar conteo de series' : 'Recontar series'}</button>
          </div>
        </form>
      )}
    </>
  );
}

/** «Añadir lote encontrado»: un lote que no estaba en la lista (existente o nuevo) de un artículo del alcance. */
function LoteEncontrado({ inv, cerrar, alAnadir }: { inv: InventarioFisicoDetalle; cerrar: () => void; alAnadir: () => void }): ReactNode {
  const [art, setArt] = useState<OpcionArticuloStock | null>(null);
  const [lote, setLote] = useState<OpcionLote | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const campos = erroresDe(error);
  async function anadir(): Promise<void> {
    setError(null);
    setEnviando(true);
    try {
      await api.post(`/api/inventario/inventarios/${inv.id}/lotes`, { idLote: lote?.id ?? null });
      alAnadir();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }
  return (
    <Modal titulo="Añadir lote encontrado" cerrar={cerrar}
      pie={<><button onClick={cerrar}>Cancelar</button><button className="principal" disabled={lote === null || enviando} onClick={() => void anadir()}>Añadir al conteo</button></>}>
      <p className="suave">Para un lote que está físicamente pero no aparecía en la lista. Se añadirá con stock teórico 0 en este almacén (o el que tenga) y lo cuentas después.</p>
      {error !== null && Object.keys(campos).length === 0 && <ErrorCaja error={error} />}
      <Buscador<OpcionArticuloStock> etiqueta="Artículo" valor={art} alElegir={(a) => { setArt(a); setLote(null); }}
        buscar={async (t) => (await buscarArticulos(inv.almacen.id)(t)).filter((a) => a.trazabilidad?.controlaLotes === true)}
        clave={(a) => a.id} texto={nombreArticulo} contexto={inv.almacen.id} sinResultados="Ningún artículo con lotes configurado en este almacén." />
      {art !== null && (
        <SelectorLote articulo={{ ...art, controlaCaducidad: art.trazabilidad?.controlaCaducidad }} valor={lote} alElegir={setLote} error={campos['idLote']} />
      )}
    </Modal>
  );
}

function Conteo({ inv, alCambiar }: { inv: InventarioFisicoDetalle; alCambiar: () => void }): ReactNode {
  const { puede } = useSesion();
  const [filtro, setFiltro] = useState<'TODAS' | 'PENDIENTES' | 'CONTADAS' | 'RECONTADAS'>(inv.estado === 'EN_CONTEO' ? 'PENDIENTES' : 'TODAS');
  const [texto, setTexto] = useState('');
  const [pagina, setPagina] = useState(1);
  const [aviso, setAviso] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [loteEncontrado, setLoteEncontrado] = useState(false);
  const [lectura, setLectura] = useState<LecturaConteo | null>(null);
  //  Series leídas en esta sesión por línea: una serie repetida es un error, no una unidad más.
  const seriesLeidas = useRef(new Map<string, Set<string>>());
  const inputs = useRef(new Map<string, HTMLInputElement>());
  const enfocar = useRef<string | null>(null);
  const datos = useDatos(() => {
    const p = new URLSearchParams({ filtro, pagina: String(pagina), porPagina: String(POR_PAGINA_CONTEO) });
    if (texto !== '') p.set('texto', texto);
    return api.get<{ filas: LineaInventarioFisico[]; total: number }>(`/api/inventario/inventarios/${inv.id}/lineas?${p.toString()}`);
  }, [inv.id, filtro, texto, pagina, version]);
  //  F13.4: el técnico cuenta SU furgoneta (contar_movil; el servidor no le deja ver ningún otro inventario).
  const cuenta = inv.estado === 'EN_CONTEO' && (puede('inventarios:contar') || puede('inventarios:contar_movil'));
  async function leerEnConteo(l: Lectura): Promise<string | null> {
    const r = l.recurso;
    if (r.articulo === null) return 'La lectura no identifica un artículo.';
    if (r.articulo.controlaLotes && r.lote === null) return `${r.articulo.codigoNegocio} se cuenta por lotes: escanea la etiqueta del lote.`;
    const codigo = r.lote?.codigo ?? r.articulo.codigo;
    const p = new URLSearchParams({ filtro: 'TODAS', texto: codigo, pagina: '1', porPagina: '50' });
    const lineas = (await api.get<{ filas: LineaInventarioFisico[] }>(`/api/inventario/inventarios/${inv.id}/lineas?${p.toString()}`)).filas;
    const linea = lineas.find((x) => x.articulo.id === r.articulo!.id && (r.lote === null || x.lote?.id === r.lote.id));
    if (linea === undefined) {
      return r.lote !== null
        ? `El lote ${r.lote.codigo} no está en este inventario. Si lo has encontrado, usa «Añadir lote encontrado».`
        : `${r.articulo.codigoNegocio} no está en este inventario.`;
    }
    const serie = linea.serializado === true ? (r.serie?.numero ?? l.seriePendiente) : null;
    if (linea.serializado === true) {
      if (serie === null) return `${r.articulo.codigoNegocio} se cuenta por número de serie: escanea la serie.`;
      const ya = seriesLeidas.current.get(linea.id) ?? new Set<string>();
      if (ya.has(serie.toUpperCase())) return `La serie ${serie} ya se ha contado.`;
      ya.add(serie.toUpperCase());
      seriesLeidas.current.set(linea.id, ya);
    }
    setTexto(codigo);
    setFiltro('TODAS');
    setPagina(1);
    setLectura({ idLinea: linea.id, cantidad: l.cantidad, serie, k: Date.now() + Math.random() });
    return null;
  }
  //  «Guardar y siguiente»: tras recargar, el foco va a la siguiente pendiente.
  useEffect(() => {
    const filas = datos.datos?.filas;
    if (filas === undefined || enfocar.current === null) return;
    const desde = enfocar.current;
    enfocar.current = null;
    const i = filas.findIndex((l) => l.id === desde);
    const siguiente = [...filas.slice(i + 1), ...filas.slice(0, Math.max(0, i))].find((l) => l.estadoConteo === 'PENDIENTE' && l.id !== desde);
    if (siguiente === undefined) { setAviso(filas.length > 0 ? 'No quedan artículos pendientes en esta página.' : null); return; }
    setAviso(null);
    const el = inputs.current.get(siguiente.id);
    el?.focus();
    el?.scrollIntoView?.({ block: 'center' });
  }, [datos.datos]);
  return (
    <section className="panel" aria-label="Conteo">
      <div className="panel-cuerpo">
        <Progreso contadas={inv.resumen.contadas} total={inv.resumen.total} />
        {inv.conteoCiego && inv.estado === 'EN_CONTEO' && <p className="suave pequena">Conteo ciego: cuenta lo que hay, sin referencia del sistema.</p>}
        {cuenta && (
          <div className="fila" style={{ flexWrap: 'wrap' }}>
            <button type="button" className="pequeno boton-lote-encontrado" onClick={() => setLoteEncontrado(true)}>Añadir lote encontrado</button>
          </div>
        )}
        {cuenta && (
          <Escaner titulo="Contar escaneando" contexto={{ uso: 'INVENTARIO', idAlmacen: inv.almacen.id }} alLeer={leerEnConteo} />
        )}
        {loteEncontrado && <LoteEncontrado inv={inv} cerrar={() => setLoteEncontrado(false)} alAnadir={() => { setLoteEncontrado(false); setVersion((x) => x + 1); alCambiar(); }} />}
        <FiltrosResponsive contador={(filtro !== 'TODAS' ? 1 : 0) + (texto !== '' ? 1 : 0)}>
          <div className="filtros-rejilla">
            <div className="campo"><label htmlFor="ct-texto">Artículo o lote</label>
              <input id="ct-texto" type="search" placeholder="Código, descripción o lote" value={texto} onChange={(ev) => { setTexto(ev.target.value); setPagina(1); }} /></div>
            <label className="campo"><span>Mostrar</span>
              <select value={filtro} onChange={(ev) => { setFiltro(ev.target.value as typeof filtro); setPagina(1); }}>
                <option value="TODAS">Todos</option><option value="PENDIENTES">Pendientes</option><option value="CONTADAS">Contados</option><option value="RECONTADAS">Recontados</option>
              </select>
            </label>
          </div>
        </FiltrosResponsive>
        {aviso !== null && <p className="nota" role="status">{aviso}</p>}
        <Panel estado={datos}>
          {(d) => (d.filas.length === 0
            ? <Vacio titulo={filtro === 'PENDIENTES' ? 'No quedan artículos pendientes' : 'Sin artículos'}>{filtro === 'PENDIENTES' && inv.estado === 'EN_CONTEO' ? 'Ya puedes enviar el inventario a validación.' : null}</Vacio>
            : (
              <>
                <div className="lista-conteo">
                  {d.filas.map((l) => (
                    <TarjetaConteo key={l.id} inv={inv} linea={l} cuenta={cuenta} lectura={lectura?.idLinea === l.id ? lectura : null}
                      refInput={(el) => { if (el === null) inputs.current.delete(l.id); else inputs.current.set(l.id, el); }}
                      alGuardar={(_, siguiente) => { if (siguiente) enfocar.current = l.id; setVersion((x) => x + 1); alCambiar(); }} />
                  ))}
                </div>
                <Paginacion pagina={pagina} hayMas={pagina * POR_PAGINA_CONTEO < d.total} fijar={setPagina} total={d.total} porPagina={POR_PAGINA_CONTEO} />
              </>
            ))}
        </Panel>
      </div>
    </section>
  );
}

// ======================================================= ficha: diferencias

function Diferencias({ inv }: { inv: InventarioFisicoDetalle }): ReactNode {
  const [filtro, setFiltro] = useState<'TODAS' | 'CON_DIFERENCIA' | 'SIN_DIFERENCIA'>('CON_DIFERENCIA');
  const [pagina, setPagina] = useState(1);
  const [ver, setVer] = useState<LineaInventarioFisico | null>(null);
  const datos = useDatos(() => api.get<{ filas: LineaInventarioFisico[]; total: number }>(
    `/api/inventario/inventarios/${inv.id}/lineas?filtro=${filtro}&pagina=${String(pagina)}&porPagina=${String(POR_PAGINA)}`), [inv.id, filtro, pagina, inv.actualizadoEn]);
  const r = inv.resumen;
  const cerrado = inv.estado === 'CERRADO';
  return (
    <section className="panel" aria-label="Diferencias">
      <div className="panel-cuerpo">
        <div className="cifras resumen-inventario" aria-label="Resumen de la validación">
          <Cifra n={r.total} t="artículos" />
          <Cifra n={r.sinDiferencia ?? 0} t="sin diferencias" />
          <Cifra n={r.conDiferencia ?? 0} t="con diferencias" />
          <Cifra n={r.positivas ?? 0} t="diferencia positiva" />
          <Cifra n={r.negativas ?? 0} t="diferencia negativa" />
        </div>
        <FiltrosResponsive contador={filtro !== 'TODAS' ? 1 : 0}>
          <div className="filtros-rejilla">
            <label className="campo"><span>Mostrar</span>
              <select value={filtro} onChange={(ev) => { setFiltro(ev.target.value as typeof filtro); setPagina(1); }}>
                <option value="TODAS">Todos</option><option value="CON_DIFERENCIA">Con diferencias</option><option value="SIN_DIFERENCIA">Sin diferencias</option>
              </select>
            </label>
          </div>
        </FiltrosResponsive>
        <Panel estado={datos}>
          {(d) => (d.filas.length === 0 ? <Vacio titulo={filtro === 'CON_DIFERENCIA' ? 'Ninguna diferencia' : 'Sin artículos'} /> : (
            <>
              <div className="tabla-desliza">
                <table aria-label="Diferencias de inventario" className="tabla-diferencias tabla-tarjetas">
                  <thead><tr><th>Artículo</th><th className="num">Teórico</th><th className="num">Físico</th><th className="num">Diferencia</th>
                    {!cerrado && <><th className="num">Saldo actual</th><th className="num">Tras ajuste</th></>}<th>Último conteo</th><th>Conteos</th></tr></thead>
                  <tbody>
                    {d.filas.map((l) => {
                      const v = l.vigente!;
                      const u = l.unidadStock.codigo;
                      const dif = v.diferencia ?? '0';
                      const difTexto = dif.startsWith('-') || dif === '0' ? dif : `+${dif}`;
                      return (
                        <tr key={l.id}>
                          <td className="celda-titulo">{nombreArticulo(l.articulo)}{l.nConteos > 1 && <span className="etiqueta aviso">Recontado</span>}
                            {l.lote != null && <div className="pequena">Lote <span className="mono">{l.lote.codigo}</span>{l.lote.caducado && <span className="etiqueta error">Caducado</span>}</div>}
                            {l.series != null && <><ListaNumeros etiqueta="Faltan" numeros={l.series.faltantes ?? []} clase="saldo-negativo" /><ListaNumeros etiqueta="Inesperadas" numeros={l.series.inesperadas ?? []} clase="aviso-texto" /></>}</td>
                          <td className="num" data-label="Teórico">{v.teorico} {u}</td>
                          <td className="num" data-label="Físico">{v.cantidadStock} {u}</td>
                          <td className={`num dif${dif.startsWith('-') ? ' saldo-negativo' : ''}`} data-label="Diferencia">{difTexto} {u}</td>
                          {!cerrado && <>
                            <td className="num" data-label="Saldo actual">{l.saldoActual} {u}</td>
                            <td className="num" data-label="Tras ajuste">{l.saldoTrasAjuste} {u}</td>
                          </>}
                          <td className="pequena" data-label="Último conteo">{v.contadoPor.nombre}<br />{fechaHora(v.contadoEn)}</td>
                          <td className="celda-accion"><button className="pequeno" onClick={() => setVer(l)}>Ver conteos</button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Paginacion pagina={pagina} hayMas={pagina * POR_PAGINA < d.total} fijar={setPagina} total={d.total} porPagina={POR_PAGINA} />
            </>
          ))}
        </Panel>
        {ver !== null && <Modal titulo={`Conteos de ${ver.articulo.codigo}`} cerrar={() => setVer(null)}><HistorialConteos inv={inv} linea={ver} /></Modal>}
      </div>
    </section>
  );
}

function HistorialInventario({ id }: { id: string }): ReactNode {
  const TEXTO: Readonly<Record<string, string>> = {
    CREADO: 'Creado', ALCANCE_MODIFICADO: 'Alcance o configuración modificados', INICIADO: 'Iniciado', CONTEO_REGISTRADO: 'Conteo registrado',
    RECONTEO_REGISTRADO: 'Reconteo registrado', ENVIADO_VALIDACION: 'Enviado a validación', VUELTO_A_CONTEO: 'Vuelto a conteo', CERRADO: 'Cerrado', ANULADO: 'Anulado',
  };
  const h = useHistorial<EventoInventarioFisico>(`/api/inventario/inventarios/${id}/historial`);
  return (
    <div className="panel"><div className="panel-cuerpo">
      <Panel estado={h}>{(x) => (
        <ol className="lista-historial">
          {x.filas.map((e) => (
            <li key={e.id}><strong>{TEXTO[e.tipo] ?? e.tipo}</strong>
              {e.cambios !== null && <CambiosEvento cambios={e.cambios} />}
              <div className="suave pequena">{e.usuario.nombre} · {fechaHora(e.ocurridoEn)}</div></li>
          ))}
        </ol>
      )}</Panel>
      <PieHistorial historial={h} />
    </div></div>
  );
}

// ==================================================================== ficha

export function PaginaInventarioFisico(): ReactNode {
  const { id = '' } = useParams();
  const { puede } = useSesion();
  const [params, setParams] = useSearchParams();
  const inv = useDatos(() => api.get<InventarioFisicoDetalle>(`/api/inventario/inventarios/${encodeURIComponent(id)}`), [id]);
  const [dialogo, setDialogo] = useState<'iniciar' | 'enviar' | 'volver' | 'cerrar' | 'anular' | null>(null);
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const toast = useToast();
  const d = inv.datos;
  const migas = [{ texto: 'Inventario' }, { texto: 'Inventarios', a: '/inventario/inventarios' }, { texto: d?.codigo ?? '…' }];
  if (inv.error instanceof FalloApi && (inv.error.estado === 404 || inv.error.estado === 403)) {
    return <Shell migas={migas} titulo="Inventario no disponible"><div className="panel"><Vacio titulo="Este inventario no existe o no tienes acceso"><Link to="/inventario/inventarios">Volver</Link></Vacio></div></Shell>;
  }
  const porDefecto = d === null ? 'info' : d.estado === 'BORRADOR' ? 'info' : d.estado === 'EN_CONTEO' ? 'conteo' : d.veDiferencias ? 'diferencias' : 'conteo';
  const tab = params.get('tab') ?? porDefecto;
  const abierto = d !== null && ['BORRADOR', 'EN_CONTEO', 'PENDIENTE_VALIDACION'].includes(d.estado);
  async function accion(ruta: string, cuerpo: Record<string, unknown> = {}): Promise<void> {
    if (d === null) return;
    setError(null);
    setOcupado(true);
    try {
      await api.post(`/api/inventario/inventarios/${d.id}/${ruta}`, cuerpo);
      setDialogo(null);
      setMotivo('');
      //  Cambio de estado ⇒ la pestaña por defecto del nuevo estado (validación → Diferencias; conteo → Conteo).
      setParams({});
      inv.recargar();
      const hecho = EXITO_INVENTARIO[ruta];
      if (hecho !== undefined) toast.exito(hecho);
    } catch (e) {
      setDialogo(null);
      setError(e);
    } finally {
      setOcupado(false);
    }
  }
  const pestanas: [string, string][] = [['info', 'Información'], ...(d !== null && d.estado !== 'BORRADOR' ? [['conteo', 'Conteo'] as [string, string]] : []),
    ...(d?.veDiferencias === true ? [['diferencias', 'Diferencias'] as [string, string]] : []), ...(puede('inventarios:leer') ? [['historial', 'Historial'] as [string, string]] : [])];
  const incompleto = error instanceof FalloApi && error.codigo === 'INVENTARIO_INCOMPLETO';
  return (
    <Shell migas={migas} titulo={d === null ? 'Inventario' : `Inventario ${d.codigo}`}
      estado={d === null ? undefined : <Estado e={d.estado} />}
      meta={d === null ? undefined : <><span>{nombreAlmacen(d.almacen)}</span><span>{ALCANCE[d.alcance]}{d.conteoCiego ? ' · conteo ciego' : ''}</span></>}
      acciones={d === null || !abierto ? undefined : (
        //  UX-1B: la principal sigue el flujo (iniciar → enviar → cerrar); volver a conteo, secundaria; anular, en «Más acciones».
        <BarraAcciones etiqueta="Acciones del inventario"
          principal={d.estado === 'BORRADOR' && puede('inventarios:gestionar') ? <button className="principal" onClick={() => setDialogo('iniciar')}>Iniciar inventario</button>
            : d.estado === 'EN_CONTEO' && puede('inventarios:gestionar') ? <button className="principal" onClick={() => setDialogo('enviar')}>Enviar a validación</button>
            : d.estado === 'PENDIENTE_VALIDACION' && puede('inventarios:cerrar') ? <button className="principal" onClick={() => setDialogo('cerrar')}>Cerrar inventario y regularizar</button>
            : undefined}
          secundaria={d.estado === 'PENDIENTE_VALIDACION' && puede('inventarios:validar') ? <button onClick={() => setDialogo('volver')}>Volver a conteo</button> : undefined}
          mas={[puede('inventarios:gestionar') && { texto: 'Anular', alPulsar: () => setDialogo('anular'), peligro: true }]} />
      )}
      tabs={<Pestanas opciones={pestanas} actual={tab} fijar={(k) => setParams({ tab: k })} />}>
      {error !== null && <ErrorStock error={error} />}
      {incompleto && <p className="nota aviso" role="status"><button className="enlace" onClick={() => { setError(null); setParams({ tab: 'conteo' }); }}>Ver los artículos pendientes</button></p>}
      <Panel estado={inv}>
        {(x) => (
          <>
            {x.estado === 'BORRADOR' && <p className="nota aviso banner-borrador" role="status"><strong>Borrador.</strong> No afecta al stock. Al iniciarlo se fija el alcance.</p>}
            {x.estado === 'CERRADO' && (
              <p className="nota exito" role="status"><strong>Inventario cerrado.</strong> {x.operacionAjuste === null
                ? 'No había diferencias: no se generó ninguna operación.'
                : <>Operación generada: <Link to={`/inventario/operaciones/${x.operacionAjuste.id}`}>{x.operacionAjuste.codigo}</Link>.</>}</p>
            )}
            {x.estado === 'ANULADO' && <p className="nota" role="status">Anulado por {x.anuladoPor?.nombre} · {fechaHora(x.anuladoEn)} — {x.motivoAnulacion}. No movió stock.</p>}
            {tab === 'info' && (
              <>
                <section className="panel" aria-label="Información del inventario">
                  <div className="panel-cuerpo">
                    <h2 className="titulo-seccion">Información</h2>
                    <div className="datos">
                      <Dato e="Almacén" v={nombreAlmacen(x.almacen)} />
                      <Dato e="Alcance" v={ALCANCE[x.alcance]} />
                      <Dato e="Conteo ciego" v={x.conteoCiego ? 'Sí' : 'No'} />
                      <Dato e="Progreso" v={x.estado === 'BORRADOR' ? '—' : `${String(x.resumen.contadas)} / ${String(x.resumen.total)}`} />
                      <Dato e="Creado" v={`${x.creadoPor.nombre} · ${fechaHora(x.creadoEn)}`} />
                      <Dato e="Iniciado" v={x.iniciadoPor === null ? null : `${x.iniciadoPor.nombre} · ${fechaHora(x.iniciadoEn)}`} />
                      <Dato e="Enviado a validación" v={x.enviadoValidacionPor === null ? null : `${x.enviadoValidacionPor.nombre} · ${fechaHora(x.enviadoValidacionEn)}`} />
                      <Dato e="Cerrado" v={x.cerradoPor === null ? null : `${x.cerradoPor.nombre} · ${fechaHora(x.cerradoEn)}`} />
                      {x.estado !== 'BORRADOR' && <Dato e="Observaciones" v={x.observaciones} />}
                    </div>
                    {x.estado === 'BORRADOR' && puede('inventarios:gestionar') && <EditarBorrador key={x.actualizadoEn} inv={x} alCambiar={inv.recargar} />}
                  </div>
                </section>
                {x.alcance === 'PARCIAL' && <ArticulosParciales inv={x} edita={x.estado === 'BORRADOR' && puede('inventarios:gestionar')} alCambiar={inv.recargar} />}
              </>
            )}
            {tab === 'conteo' && x.estado !== 'BORRADOR' && <Conteo inv={x} alCambiar={inv.recargar} />}
            {tab === 'diferencias' && x.veDiferencias && <Diferencias inv={x} />}
            {tab === 'historial' && <HistorialInventario id={x.id} />}

            {dialogo === 'iniciar' && (
              <Modal titulo={`Iniciar ${x.codigo}`} cerrar={() => setDialogo(null)}
                pie={<><button onClick={() => setDialogo(null)}>Cancelar</button><button className="principal" disabled={ocupado} onClick={() => void accion('iniciar')}>Iniciar inventario</button></>}>
                <p>{TEXTO_INICIAR}</p>
                <ul className="lista-simple">
                  <li>Almacén: <strong>{nombreAlmacen(x.almacen)}</strong></li>
                  <li>Alcance: <strong>{ALCANCE[x.alcance]}</strong> · {x.alcance === 'TOTAL' ? 'todos los artículos configurados en este momento' : `${String(x.resumen.total)} artículo(s)`}</li>
                  <li>Conteo ciego: <strong>{x.conteoCiego ? 'Sí' : 'No'}</strong></li>
                </ul>
              </Modal>
            )}
            {dialogo === 'enviar' && (
              <Modal titulo={`Enviar ${x.codigo} a validación`} cerrar={() => setDialogo(null)}
                pie={<><button onClick={() => setDialogo(null)}>Cancelar</button><button className="principal" disabled={ocupado} onClick={() => void accion('enviar-validacion')}>Enviar a validación</button></>}>
                <p>{x.resumen.pendientes > 0 ? `Quedan ${String(x.resumen.pendientes)} artículo(s) sin contar: no se puede enviar todavía.` : 'Todos los artículos están contados. En validación ya no se registran conteos (salvo que vuelva a conteo).'}</p>
              </Modal>
            )}
            {dialogo === 'volver' && (
              <Modal titulo={`Volver a conteo ${x.codigo}`} cerrar={() => setDialogo(null)}
                pie={<><button onClick={() => setDialogo(null)}>Cancelar</button><button className="principal" disabled={ocupado || motivo.trim().length < 5} onClick={() => void accion('volver-a-conteo', { motivo })}>Volver a conteo</button></>}>
                <p>Los conteos registrados se conservan; se podrán añadir reconteos.</p>
                <div className="campo"><label htmlFor="vc-motivo">Motivo</label><textarea id="vc-motivo" rows={2} value={motivo} onChange={(ev) => setMotivo(ev.target.value)} /></div>
              </Modal>
            )}
            {dialogo === 'cerrar' && (
              <Modal titulo={`Cerrar ${x.codigo}`} cerrar={() => setDialogo(null)}
                pie={<><button onClick={() => setDialogo(null)}>Cancelar</button><button className="principal" disabled={ocupado} onClick={() => void accion('cerrar')}>Cerrar inventario y regularizar</button></>}>
                {(x.resumen.conDiferencia ?? 0) === 0
                  ? <p>No hay diferencias: el inventario se cerrará <strong>sin</strong> operación de ajuste.</p>
                  : (
                    <>
                      <p>Se generará una operación de ajuste para regularizar <strong>{x.resumen.conDiferencia}</strong> artículo(s).</p>
                      <ul className="lista-simple"><li>Entradas: {x.resumen.positivas} línea(s)</li><li>Salidas: {x.resumen.negativas} línea(s)</li></ul>
                      <p className="suave pequena">Con lotes, el ajuste se hace por lote; con series, cada faltante sale y cada inesperada entra por su número.</p>
                    </>
                  )}
                <p className="suave">{TEXTO_CIERRE}</p>
              </Modal>
            )}
            {dialogo === 'anular' && (
              <Modal titulo={`Anular ${x.codigo}`} cerrar={() => setDialogo(null)}
                pie={<><button onClick={() => setDialogo(null)}>Cancelar</button><button className="peligro" disabled={ocupado || motivo.trim().length < 5} onClick={() => void accion('anular', { motivo })}>Anular inventario</button></>}>
                <p>No se moverá stock. El alcance, los conteos y el historial se conservan.</p>
                <div className="campo"><label htmlFor="an-motivo">Motivo</label><textarea id="an-motivo" rows={2} value={motivo} onChange={(ev) => setMotivo(ev.target.value)} /></div>
              </Modal>
            )}
          </>
        )}
      </Panel>
    </Shell>
  );
}
